import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useJsonLd from '../seo/useJsonLd';
import {
  IconArrowLeft,
  IconTrophy,
  IconMapPin,
  IconChevronRight,
  IconRoute,
  IconFlag,
  IconBrandDiscord,
  IconBrandStrava,
  IconChevronDown,
  IconChevronUp,
  IconMap2,
  IconVideo,
  IconBookmark,
  IconPhoto,
  IconPlayerPlay,
  IconDiamond,
  IconRun,
  IconLock,
  IconRefresh,
  IconAlertTriangle
} from '@tabler/icons-react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import yukiguniPreview from '../assets/images/yukiguni_map_example.png';
import nakasendoCut2Preview from '../assets/images/Nakasendo_preview_cut2.png';
import sakurasPreview from '../assets/images/sakuras_maps_example.png';
import stravaConnectBtn from '../assets/strava/btn_strava_connect_with_orange.svg';
import { usePostHog } from 'posthog-js/react';
import {
  getChallengeTotalDistanceMeters,
  isChallengeParticipantComplete,
  hasChallengeTrophy,
  getLatestCompletionDate,
} from '../utils/challengeProgress';
import { CHALLENGE_CATALOG, getFinisherMedalUrl } from '../data/challengeCatalog';
import Footer from '../components/Footer';
import FinisherMedalModal from '../components/FinisherMedalModal';
import ChallengeAhaHero from '../components/ChallengeAhaHero';
import './ChallengePage.css';
const marketingSeo = require('../seo/marketingSeo');

const VIDEOS = {
  cut2Landscape: 'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2FNakasendo_cut2_landscape.mp4?alt=media',
  cut3Landscape: 'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2FNakasendo_cut3_landscape.mp4?alt=media',
  flyoverLandscape: 'https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2FNagasendo_flyover_landscape.mp4?alt=media',
};

const ASSET_URL = (file) =>
  `https://firebasestorage.googleapis.com/v0/b/mythic-maps.firebasestorage.app/o/assets%2F${file}?alt=media`;

const HERO_IMAGES = {
  'Caravan Road':       ASSET_URL('Caravan_road_hero.png'),
  'Barrier of Spears':  ASSET_URL('Barrier_of_spears_hero.png'),
  'Dolomites Traverse': ASSET_URL('Dolomites_traverse_hero.png'),
  'Annapurna Circuit':  ASSET_URL('Annapurna_circuit_hero.png'),
  'Silk Road Heights':  ASSET_URL('Silk_road_heights_hero.png'),
  'Continental Divide': ASSET_URL('Continental_divide_hero.png'),
  'Volcanic Spine':     ASSET_URL('Volcanic_spine_hero.png'),
};

const ChallengePage = ({ user, onLoginRequired }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const landingViewedRef = useRef(false);
  const [challengeData, setChallengeData] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [challengeStatus, setChallengeStatus] = useState(null); // legacy: japan-specific
  const [challengeStatsById, setChallengeStatsById] = useState({}); // id -> { distance, checkpointCount, route }
  const [challengeStatusById, setChallengeStatusById] = useState({}); // id -> 'completed' | 'joined' | null
  const [participantsById, setParticipantsById] = useState({}); // id -> participant doc data
  const [statusResolved, setStatusResolved] = useState(false); // per-user status finished resolving (gates the Aha hero so it never flashes)
  const [restartingId, setRestartingId] = useState(null);
  const [restartConfirmId, setRestartConfirmId] = useState(null); // which challenge is in the confirm modal
  const [medalModal, setMedalModal] = useState(null); // { url, title, slug, accent } for the finisher medal lightbox
  const [restartError, setRestartError] = useState(null); // error message shown inside the modal

  // Load all challenge docs in parallel (Japan still feeds showcase visuals
  // via challengeData; per-card stats come from challengeStatsById).
  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      const results = await Promise.all(
        CHALLENGE_CATALOG.map(async (c) => {
          try {
            const snap = await getDoc(doc(db, 'gpxMapConfigs', c.id));
            return snap.exists() ? { id: c.id, data: snap.data() } : { id: c.id, data: null };
          } catch (err) {
            console.error(`Error loading challenge ${c.id}:`, err);
            return { id: c.id, data: null };
          }
        })
      );
      if (cancelled) return;
      const statsMap = {};
      let japanDoc = null;
      results.forEach(({ id, data }) => {
        if (!data) return;
        if (id === 'japan') japanDoc = data;
        const distance =
          data?.route?.stats?.distance ||
          (data?.route ? getChallengeTotalDistanceMeters(data.route) / 1000 : null);
        statsMap[id] = {
          distance: distance ? Math.round(distance) : null,
          checkpointCount: data?.route?.checkpoints?.length || null,
          route: data?.route || null,
        };
      });
      setChallengeStatsById(statsMap);
      if (japanDoc) setChallengeData(japanDoc);
    };
    loadAll();
    return () => { cancelled = true; };
  }, []);

  // Per-challenge participation status (one read per challenge).
  useEffect(() => {
    if (!user?.uid) {
      setChallengeStatusById({});
      setParticipantsById({});
      setChallengeStatus(null);
      setStatusResolved(true); // logged-out is resolved: not in any challenge
      return;
    }
    const ids = Object.keys(challengeStatsById);
    if (ids.length === 0) return; // stats not loaded yet; stay unresolved
    setStatusResolved(false); // re-gate while the per-user reads run
    let cancelled = false;
    const check = async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, 'gpxMapConfigs', id, 'participants', user.uid));
            if (!snap.exists()) return [id, null, null];
            const data = snap.data();
            const route = challengeStatsById[id]?.route;
            const status = (route && isChallengeParticipantComplete(data, data.currentDistanceMeters, route))
              ? 'completed'
              : 'joined';
            return [id, status, data];
          } catch (err) {
            console.error(`Error checking status for ${id}:`, err);
            return [id, null, null];
          }
        })
      );
      if (cancelled) return;
      const statusMap = {};
      const participantMap = {};
      entries.forEach(([id, status, data]) => {
        if (status) statusMap[id] = status;
        if (data) participantMap[id] = data;
      });
      setChallengeStatusById(statusMap);
      setParticipantsById(participantMap);
      // Keep the legacy single-status state in sync for the page-view analytics event.
      setChallengeStatus(statusMap.japan || null);
      setStatusResolved(true);
    };
    check();
    return () => { cancelled = true; };
  }, [user, challengeStatsById]);

  // Derived state — the single in-progress challenge (if any) and the trophy list.
  const activeChallengeId = useMemo(() => {
    const entry = Object.entries(challengeStatusById).find(([, s]) => s === 'joined');
    return entry ? entry[0] : null;
  }, [challengeStatusById]);

  const activeChallenge = useMemo(() => {
    if (!activeChallengeId) return null;
    const catalogEntry = CHALLENGE_CATALOG.find((c) => c.id === activeChallengeId);
    const stats = challengeStatsById[activeChallengeId] || {};
    const participant = participantsById[activeChallengeId] || {};
    const totalMeters = stats.route ? getChallengeTotalDistanceMeters(stats.route) : 0;
    return {
      catalog: catalogEntry,
      progressKm: Math.round((participant.currentDistanceMeters || 0) / 1000),
      totalKm: stats.distance || (totalMeters ? Math.round(totalMeters / 1000) : null),
      progressPct: totalMeters > 0
        ? Math.min(100, Math.round(((participant.currentDistanceMeters || 0) / totalMeters) * 100))
        : 0,
    };
  }, [activeChallengeId, challengeStatsById, participantsById]);

  const trophyEntries = useMemo(() => {
    return CHALLENGE_CATALOG
      .map((c) => {
        const participant = participantsById[c.id];
        if (!hasChallengeTrophy(participant)) return null;
        return { catalog: c, completedAt: getLatestCompletionDate(participant) };
      })
      .filter(Boolean);
  }, [participantsById]);

  const featuredChallenge = useMemo(
    () => CHALLENGE_CATALOG.find((c) => c.featured) || CHALLENGE_CATALOG[0],
    []
  );

  const collectionChallenges = useMemo(
    () => CHALLENGE_CATALOG.filter((c) => c !== featuredChallenge),
    [featuredChallenge]
  );

  // The "Aha" hero features the first challenge the rider has neither started
  // nor finished AND whose config (route geometry + checkpoints) has loaded, so
  // the flyover plays cleanly from the route start. Null if every challenge is
  // started/finished or none has loaded yet.
  const ahaFeatured = useMemo(() => {
    return CHALLENGE_CATALOG.find((c) => {
      if (challengeStatusById[c.id]) return false; // 'joined' or 'completed'
      const route = challengeStatsById[c.id]?.route;
      const hasGeometry = !!(route?.coordinates?.length || route?.lngs?.length);
      return hasGeometry && Array.isArray(route?.checkpoints) && route.checkpoints.length > 0;
    }) || null;
  }, [challengeStatusById, challengeStatsById]);

  // Restart flow — two steps so we can render a styled confirm modal instead
  // of a native window.confirm(). requestRestart just opens the modal;
  // confirmRestart runs the backend call when the user clicks the modal CTA.
  const requestRestart = (challengeId) => {
    if (!user?.uid || restartingId) return;
    setRestartError(null);
    setRestartConfirmId(challengeId);
  };

  const closeRestartModal = () => {
    if (restartingId) return; // ignore close while the request is in flight
    setRestartConfirmId(null);
    setRestartError(null);
  };

  const confirmRestart = async () => {
    const challengeId = restartConfirmId;
    if (!challengeId || !user?.uid) return;
    setRestartingId(challengeId);
    setRestartError(null);
    try {
      const restartFn = httpsCallable(functions, 'restartMapChallenge');
      await restartFn({ configId: challengeId });
      posthog?.capture('challenge_restart_succeeded', { challenge_id: challengeId });
      // Re-fetch the participant doc so the UI reflects the reset immediately.
      const snap = await getDoc(doc(db, 'gpxMapConfigs', challengeId, 'participants', user.uid));
      if (snap.exists()) {
        const data = snap.data();
        setParticipantsById((prev) => ({ ...prev, [challengeId]: data }));
        setChallengeStatusById((prev) => ({ ...prev, [challengeId]: 'joined' }));
      }
      setRestartConfirmId(null);
      navigate(`/challenge/${challengeId}`);
    } catch (err) {
      const details = err?.details;
      if (details?.code === 'already-active') {
        setRestartError(`Finish ${details.activeChallengeTitle || 'your active challenge'} before restarting.`);
      } else {
        console.error('[challenge restart] failed:', err);
        setRestartError(err?.message || 'Could not restart the challenge. Please try again.');
      }
      posthog?.capture('challenge_restart_failed', { challenge_id: challengeId, error_code: details?.code || 'unknown' });
    } finally {
      setRestartingId(null);
    }
  };

  // PostHog: track challenge landing page view (once per mount)
  useEffect(() => {
    if (landingViewedRef.current) return;
    // Wait for challengeData to load; challengeStatus resolves to null for unauthenticated users
    if (!challengeData) return;
    landingViewedRef.current = true;
    posthog?.capture('challenge_landing_viewed', {
      page: 'challenge_landing',
      user_status: challengeStatus || 'not_joined',
      is_authenticated: !!user,
    });
  }, [challengeData, challengeStatus, user, posthog]);

  // Logged-out header CTA: open the same join/Strava-connect modal /maps uses.
  const handleHeaderStravaCta = () => {
    posthog?.capture('challenge_landing_strava_clicked', { page: 'challenge_landing' });
    if (!user && onLoginRequired) {
      onLoginRequired();
      return;
    }
    navigate('/create');
  };

  const faqs = marketingSeo.CHALLENGE_FAQS.map((f) => ({ question: t(f.qKey, f.q), answer: t(f.aKey, f.a) }));

  // JSON-LD + title (shared source; only injects if the static HTML lacks it).
  useJsonLd('/challenge');

  function renderChallengeCard({ c, status, stats, locked, activeTitle, hasTrophy, posthog: ph, t: tx, forceFeaturedLayout }) {
    const cardDistance = stats.distance ?? (c.id === 'japan' ? 400 : null);
    const cardCheckpoints = stats.checkpointCount ?? (c.id === 'japan' ? 24 : null);
    const cardStyle = {
      '--card-accent': c.theme.primary,
      '--card-accent-dark': c.theme.primaryDark,
      '--card-tint': c.theme.tint,
      '--card-on-accent': c.theme.onPrimary,
    };
    const showCompleted = status === 'completed' || hasTrophy;
    const medalUrl = showCompleted ? getFinisherMedalUrl(c) : null;
    const isFeatured = forceFeaturedLayout;
    return (
      <Link
        key={c.id}
        to={`/challenge/${c.id}`}
        className={`challenge-card${isFeatured ? ' featured' : ''}${locked ? ' locked' : ''}`}
        style={cardStyle}
        onClick={() => ph?.capture('challenge_card_clicked', {
          page: 'challenge_landing',
          challenge_id: c.id,
          user_status: status || 'not_joined',
          locked,
        })}
      >
        <div className="challenge-card-map">
          {c.id === 'japan' ? (
            <video
              src={VIDEOS.cut3Landscape}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={nakasendoCut2Preview}
              className="challenge-card-map-img"
            />
          ) : HERO_IMAGES[c.id] ? (
            <img
              src={HERO_IMAGES[c.id]}
              alt={c.title}
              className="challenge-card-map-img"
              loading="lazy"
            />
          ) : (
            <div className="challenge-card-map-placeholder">
              <IconRoute size={48} />
            </div>
          )}
          {locked && (
            <div className="challenge-card-lock-overlay">
              <div className="challenge-card-lock-pill">
                <IconLock size={14} />
                {tx('challengePage.lockedPill', {
                  defaultValue: 'Finish {{title}} first',
                  title: activeTitle || tx('challengePage.activeFallback', 'your active journey'),
                })}
              </div>
            </div>
          )}
          {medalUrl && (
            <img src={medalUrl} alt="" className="challenge-card-medal" loading="lazy" />
          )}
        </div>
        <div className="challenge-card-content">
          <div className="challenge-card-badges">
            {showCompleted && (
              <div className="challenge-card-badge challenge-card-badge-completed">
                <IconTrophy size={12} />
                {tx('challengePage.completed', 'Completed')}
              </div>
            )}
            {status === 'joined' && (
              <div className="challenge-card-badge challenge-card-badge-active">
                {tx('challengePage.inProgress', 'In progress')}
              </div>
            )}
          </div>
          <h3>{c.title}</h3>
          <p className="challenge-card-location">
            <IconMapPin size={14} />
            {c.subtitle}
          </p>
          <div className="challenge-card-stats">
            <div className="challenge-stat">
              <IconRoute size={16} />
              <span>{cardDistance ? `${cardDistance} km` : '—'}</span>
            </div>
            <div className="challenge-stat">
              <IconFlag size={16} />
              <span>{cardCheckpoints ?? '—'} {tx('challengePage.checkpoints', 'checkpoints')}</span>
            </div>
            <div className="challenge-stat">
              <IconPhoto size={16} />
              <span>{cardCheckpoints ?? '—'} {tx('challengePage.uniqueArtworks', 'unique artworks')}</span>
            </div>
          </div>
          <p className="challenge-card-description">{c.description}</p>
          <div className="challenge-card-footer">
            <span className="challenge-card-cta">
              {locked
                ? tx('challengePage.viewDetails', 'View details')
                : status === 'joined'
                  ? tx('challengePage.continueJourney', 'Continue')
                  : showCompleted
                    ? tx('challengePage.viewDetails', 'View details')
                    : tx('challengePage.joinChallenge', 'Join Challenge')}
              <IconChevronRight size={16} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="challenge-page">
      <div className="challenge-container">

        {/* Header — full-bleed dark */}
        <div className="challenge-header-section">
          <div className="challenge-header">
            <button className="back-button" onClick={() => navigate(-1)}>
              <IconArrowLeft size={20} />
              {t('challengePage.back', 'Back')}
            </button>
            <h1>
              <IconTrophy size={32} style={{ color: '#c4956a', marginRight: 10, flexShrink: 0 }} />
              {t('challengePage.title', 'Mythic Challenges')}
            </h1>
            <p className="challenge-subtitle">
              {t('challengePage.subtitle', 'Your everyday runs, walks, and rides carry you along legendary real-world routes. Hit checkpoints. Unlock stories. Collect artwork. No time limits. Free.')}
            </p>
            {!user && (
              <>
                <p className="challenge-connect-hook">
                  {t('challengePage.connectHook', 'Connect Strava once and every run, walk, or ride you record moves you along the journey automatically.')}
                </p>
                <button
                  type="button"
                  className="challenge-header-strava-cta"
                  onClick={handleHeaderStravaCta}
                >
                  <img src={stravaConnectBtn} alt={t('challengePage.ctaStrava', 'Connect with Strava, it’s free')} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Active Challenge Banner — only when the user is on a journey */}
        {activeChallenge?.catalog && (
          <div className="section-dark active-challenge-section">
            <div className="section-inner">
              <Link
                to={`/challenge/${activeChallenge.catalog.id}`}
                className="active-challenge-banner"
                style={{
                  '--card-accent': activeChallenge.catalog.theme.primary,
                  '--card-accent-dark': activeChallenge.catalog.theme.primaryDark,
                  '--card-tint': activeChallenge.catalog.theme.tint,
                  '--card-on-accent': activeChallenge.catalog.theme.onPrimary,
                }}
                onClick={() => posthog?.capture('active_challenge_banner_clicked', {
                  challenge_id: activeChallenge.catalog.id,
                })}
              >
                <div className="active-challenge-banner-left">
                  <div className="active-challenge-banner-label">
                    {t('challengePage.activeBannerLabel', 'Currently on your journey')}
                  </div>
                  <div className="active-challenge-banner-title">
                    {activeChallenge.catalog.title}
                  </div>
                  <div className="active-challenge-banner-progress">
                    <div className="active-challenge-banner-track">
                      <div
                        className="active-challenge-banner-fill"
                        style={{ width: `${activeChallenge.progressPct}%` }}
                      />
                    </div>
                    <div className="active-challenge-banner-progress-label">
                      {activeChallenge.progressKm} km
                      {activeChallenge.totalKm ? ` / ${activeChallenge.totalKm} km` : ''}
                    </div>
                  </div>
                </div>
                <div className="active-challenge-banner-cta">
                  {t('challengePage.continueJourney', 'Continue')}
                  <IconChevronRight size={18} />
                </div>
              </Link>
              <p className="active-challenge-rule">
                {t('challengePage.oneAtATimeRule', 'One adventure at a time. Finish this journey to unlock the others.')}
              </p>
            </div>
          </div>
        )}

        {/* Aha hero — personalized projection of the rider's last activity onto
            the next unstarted challenge. Hidden for logged-out users and for
            riders currently on a journey; gated on resolved per-user status so
            it never flashes a challenge they have actually started. */}
        {user && statusResolved && !activeChallengeId && ahaFeatured && (
          <ChallengeAhaHero
            user={user}
            challenge={ahaFeatured}
            route={challengeStatsById[ahaFeatured.id].route}
            posthog={posthog}
          />
        )}

        {/* Featured Journey — the catalog entry with featured: true */}
        {featuredChallenge && (
          <div className="section-dark featured-section">
            <div className="section-inner">
              <div className="challenge-section">
                <div className="featured-section-header">
                  <span className="featured-eyebrow">
                    {t('challengePage.featuredEyebrow', 'Featured Journey')}
                  </span>
                  <h2>{featuredChallenge.title}</h2>
                  <p className="section-description">
                    {featuredChallenge.description}
                  </p>
                </div>
                <div className="challenge-card-grid">
                  {renderChallengeCard({
                    c: featuredChallenge,
                    status: challengeStatusById[featuredChallenge.id],
                    stats: challengeStatsById[featuredChallenge.id] || {},
                    locked: !!activeChallengeId && activeChallengeId !== featuredChallenge.id,
                    activeTitle: activeChallenge?.catalog?.title,
                    hasTrophy: hasChallengeTrophy(participantsById[featuredChallenge.id]),
                    posthog,
                    t,
                    forceFeaturedLayout: true,
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trophy Cabinet — only when the user has earned at least one trophy */}
        {trophyEntries.length > 0 && (
          <div className="section-darker trophy-cabinet-section">
            <div className="section-inner">
              <div className="challenge-section">
                <div className="section-header-centered">
                  <h2>{t('challengePage.trophyCabinet', 'Your Completed Journeys')}</h2>
                  <p className="section-description">
                    {t('challengePage.trophyCabinetDesc', 'Trophies you have already earned. Restart any of them for a fresh run, anytime.')}
                  </p>
                </div>
                <div className="trophy-cabinet">
                  {trophyEntries.map(({ catalog, completedAt }) => {
                    const blockedByActive = !!activeChallengeId && activeChallengeId !== catalog.id;
                    const isRestarting = restartingId === catalog.id;
                    const medalUrl = getFinisherMedalUrl(catalog);
                    return (
                      <div
                        key={catalog.id}
                        className="trophy-card"
                        style={{
                          '--card-accent': catalog.theme.primary,
                          '--card-accent-dark': catalog.theme.primaryDark,
                          '--card-tint': catalog.theme.tint,
                          '--card-on-accent': catalog.theme.onPrimary,
                        }}
                      >
                        <div className={`trophy-card-icon${medalUrl ? ' trophy-card-icon--medal' : ''}`}>
                          {medalUrl
                            ? (
                              <button
                                type="button"
                                className="trophy-card-medal-btn"
                                onClick={() => setMedalModal({
                                  url: medalUrl,
                                  title: catalog.title,
                                  slug: catalog.slug,
                                  accent: catalog.theme.primary,
                                })}
                                aria-label={`View ${catalog.title} finisher medal`}
                              >
                                <img src={medalUrl} alt="" className="trophy-card-medal" loading="lazy" />
                              </button>
                            )
                            : <IconTrophy size={24} />}
                        </div>
                        <div className="trophy-card-body">
                          <Link to={`/challenge/${catalog.id}`} className="trophy-card-title">
                            {catalog.title}
                          </Link>
                          <div className="trophy-card-subtitle">
                            {completedAt
                              ? t('challengePage.completedOn', {
                                  defaultValue: 'Completed {{date}}',
                                  date: completedAt.toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  }),
                                })
                              : t('challengePage.completed', 'Completed')}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="trophy-restart-btn"
                          disabled={blockedByActive || isRestarting}
                          title={blockedByActive
                            ? t('challengePage.restartBlockedTooltip', {
                                defaultValue: 'Finish {{title}} first',
                                title: activeChallenge?.catalog?.title || '',
                              })
                            : undefined}
                          onClick={() => requestRestart(catalog.id)}
                        >
                          <IconRefresh size={14} />
                          {isRestarting
                            ? t('challengePage.restartInProgress', 'Restarting...')
                            : t('challengePage.restartJourney', 'Restart')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* The Seven Mountainous Wonders — the collection */}
        <div className="section-dark" id="seven-wonders">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered collection-section-header">
                <span className="collection-eyebrow">
                  {t('challengePage.collectionEyebrow', 'The Collection')}
                </span>
                <h2>{t('challengePage.sevenWondersTitle', 'The Seven Mountainous Wonders')}</h2>
                <p className="section-description">
                  {t('challengePage.sevenWondersDesc', '7 routes, 14 styles, every continent. Choose one at a time.')}
                </p>
              </div>

              <div className="challenge-card-grid">
                {collectionChallenges.map((c) => renderChallengeCard({
                  c,
                  status: challengeStatusById[c.id],
                  stats: challengeStatsById[c.id] || {},
                  locked: !!activeChallengeId && activeChallengeId !== c.id,
                  activeTitle: activeChallenge?.catalog?.title,
                  hasTrophy: hasChallengeTrophy(participantsById[c.id]),
                  posthog,
                  t,
                  forceFeaturedLayout: false,
                }))}
              </div>
            </div>
          </div>
        </div>

        {/* 2. What You Get — darker section */}
        <div className="section-darker">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('challengePage.whatYouGet', 'What You Get')}</h2>
                <p className="section-description">
                  {t('challengePage.whatYouGetDesc', 'A fitness challenge that turns every workout into an adventure — and every checkpoint into a reward.')}
                </p>
              </div>

              {/* Showcase Item 1: 3D Challenge Map */}
              <div className="showcase-feature">
                <div className="showcase-visual showcase-visual-dark">
                  <video
                    src={VIDEOS.cut2Landscape}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={nakasendoCut2Preview}
                    className="showcase-visual-img"
                  />
                </div>
                <div className="showcase-text">
                  <div className="showcase-label">
                    <IconMap2 size={18} />
                    <span>{t('challengePage.showcase3dLabel', '3D Challenge Map')}</span>
                  </div>
                  <h3>{t('challengePage.showcase3dTitle', 'Watch your progress cross mountains and valleys in real-time')}</h3>
                  <p>{t('challengePage.showcase3dDesc', 'Your challenge route unfolds across a stunning 3D terrain map. Watch your progress carve through mountains and valleys as you conquer each section. See fellow adventurers moving alongside you on the same quest.')}</p>
                </div>
              </div>

              {/* Showcase Item 2: Video Flyover */}
              <div className="showcase-feature reverse">
                <div className="showcase-visual showcase-visual-video">
                  <video
                    src={VIDEOS.flyoverLandscape}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    className="showcase-visual-img"
                  />
                </div>
                <div className="showcase-text">
                  <div className="showcase-label">
                    <IconVideo size={18} />
                    <span>{t('challengePage.showcaseFlyoverLabel', 'Video Flyover')}</span>
                  </div>
                  <h3>{t('challengePage.showcaseFlyoverTitle', 'Earn a cinematic flyover for every activity')}</h3>
                  <p>{t('challengePage.showcaseFlyoverDesc', 'Every activity you log triggers a cinematic 3D flyover of the terrain you just conquered. A personal highlight reel delivered to your inbox, ready to share and prove what you are made of.')}</p>
                </div>
              </div>

              {/* Showcase Item 3: Checkpoint Stories */}
              <div className="showcase-feature">
                <div className="showcase-visual showcase-visual-stories">
                  {challengeData?.route?.checkpoints?.[6]?.imageUrl ? (
                    <img
                      src={challengeData.route.checkpoints[6].imageUrl}
                      alt={challengeData.route.checkpoints[6].name || 'Checkpoint story'}
                      className="showcase-visual-img"
                    />
                  ) : (
                    <div className="showcase-stories-fallback">
                      <div className="showcase-checkpoint-card">
                        <div className="showcase-checkpoint-number">7</div>
                        <div className="showcase-checkpoint-info">
                          <span className="showcase-checkpoint-name">Magome-juku</span>
                          <span className="showcase-checkpoint-subtitle">{t('challengePage.showcaseCheckpointUnlocked', 'Story unlocked')}</span>
                        </div>
                      </div>
                      <div className="showcase-checkpoint-card faded">
                        <div className="showcase-checkpoint-number">8</div>
                        <div className="showcase-checkpoint-info">
                          <span className="showcase-checkpoint-name">Tsumago-juku</span>
                          <span className="showcase-checkpoint-subtitle">12.4 km {t('challengePage.showcaseCheckpointAway', 'away')}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="showcase-text">
                  <div className="showcase-label">
                    <IconBookmark size={18} />
                    <span>{t('challengePage.showcaseCheckpointsLabel', 'Checkpoint Stories')}</span>
                  </div>
                  <h3>{t('challengePage.showcaseCheckpointsTitle', 'Unlock the real history behind every checkpoint')}</h3>
                  <p>{t('challengePage.showcaseCheckpointsDesc', 'Each checkpoint unlocks a narrative shaped by the real history and culture of that location. The harder you push, the richer the story. Earn diamonds at every milestone to spend on Mythic Maps.')}</p>
                </div>
              </div>

              {/* Showcase Item 4: Activity Log */}
              <div className="showcase-feature reverse">
                <div className="showcase-visual showcase-visual-activity">
                  <img
                    src={yukiguniPreview}
                    alt="Mythic Map example"
                    className="showcase-visual-img"
                  />
                </div>
                <div className="showcase-text">
                  <div className="showcase-label">
                    <IconPhoto size={18} />
                    <span>{t('challengePage.showcaseActivityLabel', 'Activity Log')}</span>
                  </div>
                  <h3>{t('challengePage.showcaseActivityTitle', 'Every workout becomes a work of art')}</h3>
                  <p>{t('challengePage.showcaseActivityDesc', 'Every activity that counts toward your challenge is automatically transformed into a Mythic Map in a curated art style. Trained style engines with real aesthetic craft, not generic noise. Build a visual archive of your journey, completely free.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. How It Works — gradient section */}
        <div className="section-gradient">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('challengePage.howItWorks', 'How It Works')}</h2>
              </div>
              <div className="hiw-cards">
                {/* Step 1: Connect */}
                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-connect">
                    <img src={stravaConnectBtn} alt="Connect with Strava" className="hiw-strava-btn" />
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">1</div>
                    <h3>{t('challengePage.hiwConnectTitle', 'Connect Strava')}</h3>
                    <p>{t('challengePage.hiwConnectDesc', 'Link your Strava account in one tap. Running, cycling, walking, hiking, every activity with distance counts toward your quest.')}</p>
                  </div>
                </div>

                {/* Step 2: Move */}
                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-progress">
                    <div className="hiw-progress-mock">
                      <div className="hiw-progress-track">
                        <div className="hiw-progress-fill" />
                        <div className="hiw-progress-dot">
                          <IconRun size={14} />
                        </div>
                      </div>
                      <div className="hiw-progress-labels">
                        <span>0 km</span>
                        <span className="hiw-progress-you">{t('challengePage.hiwProgressYou', 'You: 162 km')}</span>
                        <span>400 km</span>
                      </div>
                    </div>
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">2</div>
                    <h3>{t('challengePage.hiwMoveTitle', 'Move at your own pace')}</h3>
                    <p>{t('challengePage.hiwMoveDesc', 'No time limit. No minimum distance. Your everyday workouts stack up and carry you forward along the route. The challenge adapts to you, not the other way around.')}</p>
                  </div>
                </div>

                {/* Step 3: Unlock */}
                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-rewards">
                    {challengeData?.route?.checkpoints?.[14]?.imageUrl ? (
                      <img
                        src={challengeData.route.checkpoints[14].imageUrl}
                        alt={challengeData.route.checkpoints[14].name || 'Checkpoint reward'}
                        className="hiw-visual-img"
                      />
                    ) : (
                      <div className="hiw-reward-mock">
                        <div className="hiw-reward-icon">
                          <IconDiamond size={24} color="#8b9dc3" />
                        </div>
                        <div className="hiw-reward-text">
                          <span className="hiw-reward-title">{t('challengePage.hiwRewardCheckpoint', 'Checkpoint reached!')}</span>
                          <span className="hiw-reward-subtitle">Magome-juku · +1 {t('challengePage.hiwRewardDiamonds', 'diamond')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">3</div>
                    <h3>{t('challengePage.hiwDiscoverTitle', 'Unlock rewards and surprises')}</h3>
                    <p>{t('challengePage.hiwDiscoverDesc', 'Reach checkpoints to unlock stories, earn diamonds, and receive cinematic flyovers. Plus, random encounters along the way keep things unpredictable.')}</p>
                  </div>
                </div>

                {/* Step 4: Collect */}
                <div className="hiw-card">
                  <div className="hiw-visual hiw-visual-maps">
                    <img src={sakurasPreview} alt="Mythic Map" className="hiw-map-img" />
                    <div className="hiw-map-overlay">
                      <span>{t('challengePage.hiwMapOverlay', 'Your Mythic Map')}</span>
                    </div>
                  </div>
                  <div className="hiw-content">
                    <div className="hiw-badge">4</div>
                    <h3>{t('challengePage.hiwCollectTitle', 'Collect your maps')}</h3>
                    <p>{t('challengePage.hiwCollectDesc', 'Every contributing activity becomes a unique Mythic Map in the challenge art style. Digital artifacts that live on your phone, your wall, and your profile.')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA — scrolls back up to the collection */}
        <div className="section-inner challenge-cta-wrapper">
          <div className="challenge-cta-banner">
            <button
              type="button"
              className="challenge-cta-button"
              onClick={() => {
                posthog?.capture('challenge_cta_clicked', {
                  page: 'challenge_landing',
                  location: 'after_how_it_works',
                  destination: 'collection',
                });
                document.getElementById('seven-wonders')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }}
            >
              {t('challengePage.ctaPickJourney', 'Pick your journey')}
              <IconChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Community — Stay in the Loop */}
        <div className="section-dark">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('challengePage.stayInTheLoop', 'Stay in the Loop')}</h2>
                <p className="section-description">
                  {t('challengePage.stayInTheLoopDesc', 'Vote on the next route, share your maps, and adventure with the community.')}
                </p>
              </div>

              <div className="community-cards">
                <a
                  href="https://discord.gg/neXEXTcQpW"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card discord-card"
                  onClick={() => posthog?.capture('challenge_community_link_clicked', { page: 'challenge_landing', platform: 'discord' })}
                >
                  <div className="community-card-icon discord-icon">
                    <IconBrandDiscord size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('challengePage.discordTitle', 'Vote on Discord')}</h3>
                    <p>{t('challengePage.discordDesc', 'Suggest routes and vote for the next challenge with the community.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="https://www.strava.com/clubs/mythicmaps"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card strava-card"
                  onClick={() => posthog?.capture('challenge_community_link_clicked', { page: 'challenge_landing', platform: 'strava_club' })}
                >
                  <div className="community-card-icon strava-icon">
                    <IconBrandStrava size={24} />
                  </div>
                  <div className="community-card-text">
                    <h3>{t('challengePage.stravaClubTitle', 'Strava Club')}</h3>
                    <p>{t('challengePage.stravaClubDesc', 'Join the Mythic Maps club to connect with other adventurers.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="https://www.reddit.com/r/mythicmapsai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="community-card reddit-card"
                  onClick={() => posthog?.capture('challenge_community_link_clicked', { page: 'challenge_landing', platform: 'reddit' })}
                >
                  <div className="community-card-icon reddit-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF4500">
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z"/>
                    </svg>
                  </div>
                  <div className="community-card-text">
                    <h3>{t('challengePage.redditTitle', 'Reddit Community')}</h3>
                    <p>{t('challengePage.redditDesc', 'Share your maps and discuss the challenge with fellow adventurers.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>

                <a
                  href="mailto:contact@mythicmaps.ai"
                  className="community-card email-card"
                  onClick={() => posthog?.capture('challenge_community_link_clicked', { page: 'challenge_landing', platform: 'email' })}
                >
                  <div className="community-card-icon email-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c4956a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2"/>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                  </div>
                  <div className="community-card-text">
                    <h3>{t('challengePage.emailTitle', 'Email Us')}</h3>
                    <p>{t('challengePage.emailDesc', 'Questions, ideas, or feedback? Drop us a line anytime.')}</p>
                  </div>
                  <IconChevronRight size={18} className="community-card-arrow" />
                </a>
              </div>

            </div>
          </div>
        </div>

        {/* 5. FAQ — default section */}
        <div className="section-default">
          <div className="section-inner">
            <div className="challenge-section">
              <div className="section-header-centered">
                <h2>{t('challengePage.faqTitle', 'Frequently Asked Questions')}</h2>
              </div>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`faq-item ${openFaq === index ? 'open' : ''}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => {
                        const isOpening = openFaq !== index;
                        setOpenFaq(isOpening ? index : null);
                        if (isOpening) {
                          posthog?.capture('challenge_faq_toggled', { page: 'challenge_landing', faq_index: index, faq_question: faq.question });
                        }
                      }}
                    >
                      <span>{faq.question}</span>
                      {openFaq === index ? (
                        <IconChevronUp size={20} />
                      ) : (
                        <IconChevronDown size={20} />
                      )}
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Footer />

      </div>

      {/* Restart confirmation modal */}
      {restartConfirmId && (() => {
        const target = CHALLENGE_CATALOG.find((c) => c.id === restartConfirmId);
        if (!target) return null;
        const isRestarting = restartingId === target.id;
        return (
          <div className="challenge-modal-overlay" onClick={closeRestartModal}>
            <div className="challenge-modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{t('challengePage.restartModalTitle', {
                defaultValue: 'Restart {{title}}?',
                title: target.title,
              })}</h2>
              <p className="challenge-modal-body">
                {t('challengePage.restartModalBody', 'Your completion trophy stays in your collection. Progress resets to zero kilometers and you can run the route again.')}
              </p>
              {restartError && (
                <div className="challenge-modal-error">
                  <IconAlertTriangle size={16} />
                  <span>{restartError}</span>
                </div>
              )}
              <div className="challenge-modal-actions">
                <button
                  type="button"
                  className="challenge-modal-cancel"
                  onClick={closeRestartModal}
                  disabled={isRestarting}
                >
                  {t('challengePage.restartModalCancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  className="challenge-modal-confirm"
                  onClick={confirmRestart}
                  disabled={isRestarting}
                >
                  {isRestarting
                    ? t('challengePage.restartInProgress', 'Restarting...')
                    : t('challengePage.restartModalConfirm', 'Restart journey')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {medalModal && (
        <FinisherMedalModal
          medalUrl={medalModal.url}
          title={medalModal.title}
          slug={medalModal.slug}
          accent={medalModal.accent}
          onClose={() => setMedalModal(null)}
        />
      )}
    </div>
  );
};

export default ChallengePage;
